import { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, Check } from 'lucide-react';
import { api } from '../../lib/api';

interface PickerEntry {
  id: string;
  title: string;
  slug: string | null;
  publishedAt: string | null;
}

interface RelationPickerModalProps {
  open: boolean;
  spaceId: string;
  targetCollectionId: string;
  multiple: boolean;
  selectedIds: string[];
  onConfirm: (selectedIds: string[]) => void;
  onClose: () => void;
}

export function RelationPickerModal({
  open,
  spaceId,
  targetCollectionId,
  multiple,
  selectedIds,
  onConfirm,
  onClose,
}: RelationPickerModalProps) {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [pending, setPending] = useState<string[]>(selectedIds);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setPending(selectedIds);
      setQuery('');
      setDebouncedQuery('');
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query), 250);
    return () => clearTimeout(t);
  }, [query]);

  const { data, isLoading } = useQuery({
    queryKey: ['relation-picker', spaceId, targetCollectionId, debouncedQuery],
    queryFn: () =>
      api
        .get<{ items: PickerEntry[]; total: number }>(
          `/api/v1/cms/spaces/${spaceId}/collections/${targetCollectionId}/entries/picker`,
          { params: { q: debouncedQuery, limit: 20 } },
        )
        .then((r) => r.data),
    enabled: open && !!targetCollectionId,
    staleTime: 30_000,
  });

  const items = data?.items ?? [];

  function toggleItem(id: string) {
    if (multiple) {
      setPending((prev) =>
        prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
      );
    } else {
      onConfirm([id]);
    }
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-zinc-900">Select entry</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-zinc-400 hover:text-zinc-600 text-xl leading-none"
          >
            ×
          </button>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-2.5 top-2 w-4 h-4 text-zinc-400" />
          <input
            ref={inputRef}
            className="w-full border border-zinc-200 rounded-lg text-sm pl-8 pr-3 h-9 focus:outline-none focus:ring-2 focus:ring-zinc-800"
            placeholder="Search entries…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>

        {/* Results */}
        <div className="max-h-72 overflow-y-auto space-y-1">
          {isLoading && (
            <p className="text-xs text-zinc-400 text-center py-6">Loading…</p>
          )}
          {!isLoading && items.length === 0 && (
            <p className="text-xs text-zinc-400 text-center py-6">No entries found.</p>
          )}
          {items.map((item) => {
            const isSelected = pending.includes(item.id);
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => toggleItem(item.id)}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left text-sm transition-colors ${
                  isSelected ? 'bg-zinc-900 text-white' : 'hover:bg-zinc-50 text-zinc-800'
                }`}
              >
                {multiple && (
                  <span
                    className={`w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center ${
                      isSelected ? 'border-white' : 'border-zinc-300'
                    }`}
                  >
                    {isSelected && <Check className="w-3 h-3" />}
                  </span>
                )}
                <span className="flex-1 truncate font-medium">{item.title}</span>
                <span className="text-xs opacity-50 truncate">{item.slug ?? ''}</span>
              </button>
            );
          })}
        </div>

        {multiple && (
          <div className="flex justify-end gap-2 pt-2 border-t border-zinc-100">
            <button
              type="button"
              onClick={onClose}
              className="px-3 h-8 text-xs font-medium border border-zinc-200 rounded-lg hover:bg-zinc-50 text-zinc-700 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => onConfirm(pending)}
              className="px-3 h-8 text-xs font-medium bg-zinc-900 text-white rounded-lg hover:bg-zinc-700 transition-colors"
            >
              Confirm ({pending.length} selected)
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
